import React, { useState } from 'react';
import Modal from '@components/widgets/Modal';
import Button from '@components/forms/Button';
import { BULK_ADD_LENDER_BROKERS, LIST_LENDER_BROKERS, post } from '@services';
import { useAuthStore, useBrokerStore } from '@stores';
import { ReactComponent as ErrorIcon } from '@assets/icons/info_error_light.svg';
import WarningIcon from '@assets/icons/WarningIcon';

type Props = {
  open: boolean;
  onClose: () => void;
};

const BulkAddBrokersModal: React.FC<Props> = ({ open, onClose }) => {
  const [emails, setEmails] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { setBrokerList } = useBrokerStore();
  const { selectedOrganisationId } = useAuthStore();
  const validateEmails = (): boolean => {
    const emailList = emails
      .split('\n')
      .map((email) => email.trim())
      .filter((email) => email !== '');

    if (emailList.length === 0) {
      setValidationErrors(['Please enter at least one email address.']);
      return false;
    }

    if (emailList.length > 1000) {
      setValidationErrors([
        'You can only add up to 1000 email addresses at once.'
      ]);
      return false;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const invalidEmails = emailList.filter((email) => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      setValidationErrors([
        'Some email addresses are incorrectly formatted. Please correct the following before continuing:',
        ...invalidEmails
      ]);
      return false;
    }

    setValidationErrors([]);
    return true;
  };

  const handleSubmit = async () => {
    if (!validateEmails()) {
      return;
    }

    setIsSubmitting(true);
    const userId = localStorage.getItem('userId');
    const lenders = localStorage.getItem(`lenders_${userId}`);

    if (lenders) {
      const lenderOrganisationId = selectedOrganisationId;

      const emailList = emails
        .split('\n')
        .map((email) => email.trim())
        .filter((email) => email !== '');

      try {
        await post(
          BULK_ADD_LENDER_BROKERS,
          {
            lenderOrganisationId,
            emails: emailList
          },
          false
        );

        // Refresh broker list
        const brokerListResponse = await post(LIST_LENDER_BROKERS, {
          lenderOrganisationId
        });
        setBrokerList(brokerListResponse.lenderBrokers);

        setEmails('');
        setShowSuccessMessage(true);

        setTimeout(() => {
          setShowSuccessMessage(false);
          onClose();
        }, 4000);
      } catch (error) {
        console.error('Error adding brokers:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Modal title="Bulk Add Brokers" size="md" open={open} onClose={onClose}>
      <div className="text-[14px]">
        <p className="text-gray-600 mb-4 text-[14px]">
          Paste or type one email address per line. Each email will receive an
          invitation to join Authentik8 and complete their profile setup.
        </p>

        <div className="mb-4">
          <p className="text-[14px] font-medium text-grey-600 mb-1.5">
            Example:
          </p>
          <div className="bg-[#EAECF0] p-3 rounded text-sm text-grey-600">
            <p>alex@example.com</p>
            <p>jamie@financegroup.au</p>
            <p>broker.one@domain.com</p>
          </div>
        </div>

        <div className="text-sm font-medium text-grey-600 mb-1.5">
          <WarningIcon
            color="#475467"
            style={{ 
              display: 'inline-block',
              marginRight: '5px',
              verticalAlign: 'middle'
            }}
          />
          <span style={{ display: 'inline', lineHeight: '1.5' }}>
            Ensure all emails are valid. Invalid entries will be skipped and not processed.
          </span>
        </div>

        <div className="mt-6 mb-4">
          <p className="text-sm font-medium text-grey-700 mb-1.5">
            Broker email addresses
          </p>
          <textarea
            placeholder="Enter email addresses, one per line"
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            className="w-full h-40 p-3 border border-grey-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-grey-900"
          />

          {validationErrors.length > 0 && (
            <div className="mt-2 text-error-light text-sm">
              {validationErrors.map((error, index) => (
                <div key={index} className="flex items-start mt-1">
                  {index === 0 && (
                    <ErrorIcon className="mr-1 mt-0.5 flex-shrink-0" />
                  )}
                  <span className={index === 0 ? '' : 'ml-5'}>{error}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6">
          <div className="h-4 w-full text-right mb-2">
            {showSuccessMessage && (
              <div className="mt-4 text-green-500">Invites sent!</div>
            )}
          </div>
          <div className="flex gap-4 justify-center lg:justify-end">
            <Button
              className="font-semibold bg-white border-grey-300 text-grey-700 w-auto capitalize"
              variant="outlined"
              onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="font-semibold bg-primary w-auto capitalize"
              onClick={handleSubmit}
              disabled={isSubmitting}>
              {isSubmitting ? 'Processing...' : 'Add Brokers'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BulkAddBrokersModal;
